/**
 * Pagination — first / prev / numbered pages / next / last.
 *
 * Compact, accessible pagination control matching the NoJo Enterprise
 * design language. Behaves like classic search-result paginators:
 *
 *   ◀◀  ◀   1   2   3 … 8   9   10   ▶  ▶▶
 *               ^current
 *
 * Always shows:
 *   - First / Last buttons (chevron-double)
 *   - Prev / Next buttons (single chevron)
 *   - Current page + 2 neighbors on each side
 *   - First/last page numbers always visible when the window doesn't
 *     reach them (separated by an ellipsis)
 *
 * Desktop ergonomics (Feb 2026 owner directive):
 *   - Larger labeled "← Previous" / "Next →" pills flank the numeric
 *     strip on md+ screens (mobile keeps the compact chevron-only form).
 *   - Two **fixed** floating chevron buttons hover at the right and
 *     left viewport edges on lg+ screens so visitors can advance pages
 *     without ever scrolling back to the bottom bar. They auto-hide at
 *     the first / last page.
 *
 * Props:
 *   page           number  current 0-indexed page
 *   totalPages     number  ≥ 1
 *   onChange(n)    fn      called with the new 0-indexed page
 *   disabled       bool    optional — locks all controls (e.g. while loading)
 *   testIdPrefix   string  for stable data-testids
 *   accent         string  themable accent color (default purple)
 *   ariaLabel      string  accessible name for the nav wrapper
 *   isJa           bool    when true, show JA labels for the big buttons
 *   showFloating   bool    set to false to suppress the fixed side buttons
 */

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

const NEIGHBORS = 1; // pages on each side of the current page in the window

function buildPageWindow(current, total) {
  // Returns an array of page numbers (0-indexed) AND `"…"` separators.
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);

  const first = 0;
  const last = total - 1;
  const left = Math.max(first + 1, current - NEIGHBORS);
  const right = Math.min(last - 1, current + NEIGHBORS);

  const out = [first];
  if (left > first + 1) out.push("ellipsis-l");
  for (let p = left; p <= right; p++) out.push(p);
  if (right < last - 1) out.push("ellipsis-r");
  out.push(last);
  return out;
}

export default function Pagination({
  page,
  totalPages,
  onChange,
  disabled = false,
  testIdPrefix = "pagination",
  accent = "#5B2E83",
  ariaLabel = "Pagination",
  isJa = false,
  showFloating = true,
}) {
  if (totalPages <= 1) return null;
  const atFirst = page <= 0 || disabled;
  const atLast = page >= totalPages - 1 || disabled;
  const window_ = buildPageWindow(page, totalPages);

  const ctrlBase =
    "inline-flex items-center justify-center h-11 min-w-[44px] px-2.5 md:h-9 md:min-w-[36px] md:px-2 rounded-full text-[14px] md:text-[12.5px] font-semibold tracking-tight transition-colors select-none";

  // Mobile-only larger Prev / Next pills (outlined Prev + filled accent Next)
  // so the primary "go forward" action is unmistakable on phones.
  const mobilePillBase =
    "md:hidden inline-flex items-center justify-center gap-1 h-12 px-5 rounded-full text-[14px] font-bold tracking-tight transition-all select-none";
  const mobileNextActive = "text-white shadow-[0_8px_20px_-8px_rgba(15,29,45,0.45)] hover:-translate-y-0.5";
  const mobilePrevActive = "bg-white ring-2 text-[#0F1D2D] hover:bg-[#F8F9FA]";
  const mobilePillDisabled = "bg-[#F1F5F9] text-[#94A3B8] ring-0 cursor-not-allowed shadow-none";

  // Large labeled pill — desktop only.
  const bigPillBase =
    "hidden md:inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full text-[14px] font-bold tracking-tight transition-all select-none";
  const bigPillActive = "bg-[#5B2E83] text-white hover:bg-[#3F1F5B] hover:-translate-y-0.5 hover:shadow-[0_14px_32px_-12px_rgba(91,46,131,0.4)]";
  const bigPillDisabled = "bg-[#F1F5F9] text-[#94A3B8] cursor-not-allowed";

  return (
    <>
      <nav
        aria-label={ariaLabel}
        data-testid={testIdPrefix}
        className="flex items-center justify-center gap-2 md:gap-4 flex-wrap"
      >
        {/* ════════ Large labeled PREVIOUS pill — desktop only ════════ */}
        <button
          type="button"
          disabled={atFirst}
          onClick={() => onChange(page - 1)}
          data-testid={`${testIdPrefix}-prev-large`}
          aria-label={isJa ? "前のページ" : "Previous page"}
          className={`${bigPillBase} ${atFirst ? bigPillDisabled : bigPillActive}`}
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={3} />
          <span>{isJa ? "前へ" : "Previous"}</span>
        </button>

        {/* ════════ Compact numeric strip (always shown) ════════ */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* First — desktop only (mobile relies on the labeled Prev pill) */}
          <button
            type="button"
            disabled={atFirst}
            onClick={() => onChange(0)}
            data-testid={`${testIdPrefix}-first`}
            aria-label="First page"
            title="First page"
            className={`${ctrlBase} hidden md:inline-flex ${
              atFirst
                ? "text-[#CBD5E1] cursor-not-allowed"
                : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F1D2D]"
            }`}
          >
            <ChevronsLeft className="h-4 w-4" strokeWidth={2.25} />
          </button>

          {/* Prev (mobile pill — outlined, accent-themed) */}
          <button
            type="button"
            disabled={atFirst}
            onClick={() => onChange(page - 1)}
            data-testid={`${testIdPrefix}-prev`}
            aria-label={isJa ? "前のページ" : "Previous page"}
            title={isJa ? "前のページ" : "Previous"}
            className={`${mobilePillBase} ${atFirst ? mobilePillDisabled : mobilePrevActive}`}
            style={!atFirst ? { borderColor: accent, color: accent } : undefined}
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2.75} />
            <span className="text-[13px]">{isJa ? "前へ" : "Prev"}</span>
          </button>

          {/* Page window */}
          {window_.map((p, i) => {
            if (typeof p === "string") {
              return (
                <span
                  key={p + i}
                  data-testid={`${testIdPrefix}-ellipsis-${i}`}
                  className="inline-flex items-center justify-center h-9 min-w-[20px] text-[12.5px] text-[#94A3B8] select-none"
                  aria-hidden
                >
                  …
                </span>
              );
            }
            const active = p === page;
            return (
              <button
                key={p}
                type="button"
                disabled={disabled}
                onClick={() => onChange(p)}
                data-testid={`${testIdPrefix}-page-${p + 1}`}
                aria-label={`Page ${p + 1}`}
                aria-current={active ? "page" : undefined}
                className={`${ctrlBase} font-mono-display ${
                  active
                    ? "text-white shadow-[0_4px_12px_-4px_rgba(91,46,131,0.55)]"
                    : disabled
                    ? "text-[#CBD5E1] cursor-not-allowed"
                    : "text-[#1F2937] hover:bg-[#F1F5F9]"
                }`}
                style={active ? { backgroundColor: accent } : undefined}
              >
                {p + 1}
              </button>
            );
          })}

          {/* Next (mobile pill — solid accent fill, primary CTA) */}
          <button
            type="button"
            disabled={atLast}
            onClick={() => onChange(page + 1)}
            data-testid={`${testIdPrefix}-next`}
            aria-label={isJa ? "次のページ" : "Next page"}
            title={isJa ? "次のページ" : "Next"}
            className={`${mobilePillBase} ${atLast ? mobilePillDisabled : mobileNextActive}`}
            style={!atLast ? { backgroundColor: accent } : undefined}
          >
            <span className="text-[13px]">{isJa ? "次へ" : "Next"}</span>
            <ChevronRight className="h-5 w-5" strokeWidth={2.75} />
          </button>

          {/* Last — desktop only (mobile relies on the labeled Next pill) */}
          <button
            type="button"
            disabled={atLast}
            onClick={() => onChange(totalPages - 1)}
            data-testid={`${testIdPrefix}-last`}
            aria-label="Last page"
            title="Last page"
            className={`${ctrlBase} hidden md:inline-flex ${
              atLast
                ? "text-[#CBD5E1] cursor-not-allowed"
                : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F1D2D]"
            }`}
          >
            <ChevronsRight className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>

        {/* ════════ Large labeled NEXT pill — desktop only ════════ */}
        <button
          type="button"
          disabled={atLast}
          onClick={() => onChange(page + 1)}
          data-testid={`${testIdPrefix}-next-large`}
          aria-label={isJa ? "次のページ" : "Next page"}
          className={`${bigPillBase} ${atLast ? bigPillDisabled : bigPillActive}`}
          style={!atLast ? { backgroundColor: accent } : undefined}
        >
          <span>{isJa ? "次へ" : "Next"}</span>
          <ChevronRight className="h-4 w-4" strokeWidth={3} />
        </button>
      </nav>

      {/* ════════════════════════════════════════════════════════════════
          FIXED FLOATING SIDE BUTTONS — lg+ screens only.
          Hover at the right and left viewport edges so visitors can
          advance pages without scrolling back to the bottom paginator.
          Hidden on mobile/tablet (where they'd cover content) and at
          the first/last page (no-op).
          ═══════════════════════════════════════════════════════════ */}
      {showFloating && !atFirst && (
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          data-testid={`${testIdPrefix}-floating-prev`}
          aria-label={isJa ? "前のページ" : "Previous page"}
          title={isJa ? "前のページ" : "Previous page"}
          className="hidden lg:flex fixed left-4 xl:left-8 top-1/2 -translate-y-1/2 z-40 h-14 w-14 items-center justify-center rounded-full bg-white text-[#0F1D2D] ring-1 ring-[#E2E8F0] shadow-[0_18px_40px_-12px_rgba(15,29,45,0.25)] hover:bg-[#F1F5F9] hover:scale-110 hover:shadow-[0_24px_56px_-12px_rgba(15,29,45,0.35)] transition-all"
        >
          <ChevronLeft className="h-7 w-7" strokeWidth={2.5} />
        </button>
      )}
      {showFloating && !atLast && (
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          data-testid={`${testIdPrefix}-floating-next`}
          aria-label={isJa ? "次のページ" : "Next page"}
          title={isJa ? "次のページ" : "Next page"}
          className="hidden lg:flex fixed right-4 xl:right-8 top-1/2 -translate-y-1/2 z-40 h-14 w-14 items-center justify-center rounded-full text-white shadow-[0_18px_40px_-12px_rgba(91,46,131,0.55)] hover:scale-110 hover:shadow-[0_24px_56px_-12px_rgba(91,46,131,0.7)] transition-all"
          style={{ backgroundColor: accent }}
        >
          <ChevronRight className="h-7 w-7" strokeWidth={2.5} />
        </button>
      )}
    </>
  );
}
